import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  organizationsService, 
  type Organization, 
  type CreateOrganizationDto, 
  type UpdateOrganizationDto,
  type OrganizationStats
} from '@/services/organizations.service'

export const useOrganizationsStore = defineStore('organizations', () => {
  // State
  const organizations = ref<Organization[]>([])
  const statistics = ref<OrganizationStats | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const activeOrganizations = computed(() => 
    organizations.value.filter(org => org.status === 'active')
  )
  
  const inactiveOrganizations = computed(() => 
    organizations.value.filter(org => org.status === 'inactive')
  )
  
  const pendingOrganizations = computed(() => 
    organizations.value.filter(org => org.status === 'pending')
  )
  
  const suspendedOrganizations = computed(() => 
    organizations.value.filter(org => org.status === 'suspended')
  )

  const organizationsByType = computed(() => {
    const grouped: Record<string, Organization[]> = {
      client: [],
      partner: [],
      internal: []
    }
    
    organizations.value.forEach(org => {
      if (grouped[org.type]) {
        grouped[org.type].push(org)
      }
    })
    
    return grouped
  })

  const organizationsBySecteur = computed(() => {
    const grouped: Record<string, Organization[]> = {}
    organizations.value.forEach(org => {
      if (org.type) {
        if (!grouped[org.type]) {
          grouped[org.type] = []
        }
        grouped[org.type].push(org)
      }
    })
    return grouped
  })

  const organizationsByTaille = computed(() => {
    const grouped: Record<string, Organization[]> = {
      small: [],
      medium: [],
      large: []
    }
    
    organizations.value.forEach(org => {
      if (org.maxEmployees) {
        if (org.maxEmployees <= 10) {
          grouped.small.push(org)
        } else if (org.maxEmployees <= 50) {
          grouped.medium.push(org)
        } else {
          grouped.large.push(org)
        }
      }
    })
    
    return grouped
  })

  // Actions
  const fetchOrganizations = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.getOrganizations()
      console.log('response fetchOrganizations: ', response)
      // Extraire les données de la structure ApiResponse
      organizations.value = response.data || []
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement des organisations'
      console.error('Erreur fetchOrganizations:', err)
    } finally {
      loading.value = false
    }
  }

  const fetchActiveOrganizations = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.getActiveOrganizations()
      // Extraire les données de la structure ApiResponse
      return response || []
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement des organisations actives'
      console.error('Erreur fetchActiveOrganizations:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchOrganizationById = async (id: string) => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.getOrganizationById(id)
      // Extraire les données de la structure ApiResponse
      return response.data
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement de l\'organisation'
      console.error('Erreur fetchOrganizationById:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchOrganizationByCode = async (code: string) => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.getOrganizationByCode(code)
      // Extraire les données de la structure ApiResponse
      return response.data
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement de l\'organisation'
      console.error('Erreur fetchOrganizationByCode:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const createOrganization = async (organizationData: CreateOrganizationDto) => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.createOrganization(organizationData)
      // Extraire les données de la structure ApiResponse
      const newOrganization = response.data
      organizations.value.push(newOrganization)
      return newOrganization
    } catch (err: any) {
      error.value = err.message || 'Erreur lors de la création de l\'organisation'
      console.error('Erreur createOrganization:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateOrganization = async (id: string, organizationData: UpdateOrganizationDto) => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.updateOrganization(id, organizationData)
      // Extraire les données de la structure ApiResponse
      const updatedOrganization = response.data
      
      const index = organizations.value.findIndex(org => org._id === id)
      if (index !== -1) {
        organizations.value[index] = updatedOrganization
      }
      
      return updatedOrganization
    } catch (err: any) {
      error.value = err.message || 'Erreur lors de la mise à jour de l\'organisation'
      console.error('Erreur updateOrganization:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const deleteOrganization = async (id: string) => {
    try {
      loading.value = true
      error.value = null
      await organizationsService.deleteOrganization(id)
      
      const index = organizations.value.findIndex(org => org._id === id)
      if (index !== -1) {
        organizations.value.splice(index, 1)
      }
    } catch (err: any) {
      error.value = err.message || 'Erreur lors de la suppression de l\'organisation'
      console.error('Erreur deleteOrganization:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchOrganizationStats = async () => {
    try {
      loading.value = true
      error.value = null
      const response = await organizationsService.getOrganizationStats()
      // Extraire les données de la structure ApiResponse
      statistics.value = response.data
      return response.data
    } catch (err: any) {
      error.value = err.message || 'Erreur lors du chargement des statistiques'
      console.error('Erreur fetchOrganizationStats:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getOrganizationById = (id: string) => {
    return organizations.value.find(org => org._id === id)
  }

  const getOrganizationByCode = (code: string) => {
    return organizations.value.find(org => org.code === code)
  }

  const clearError = () => {
    error.value = null
  }

  const reset = () => {
    organizations.value = []
    statistics.value = null
    loading.value = false
    error.value = null
  }

  return {
    // State
    organizations,
    statistics,
    loading,
    error,
    
    // Getters
    activeOrganizations,
    inactiveOrganizations,
    pendingOrganizations,
    suspendedOrganizations,
    organizationsByType,
    organizationsBySecteur,
    organizationsByTaille,
    
    // Actions
    fetchOrganizations,
    fetchActiveOrganizations,
    fetchOrganizationById,
    fetchOrganizationByCode,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    fetchOrganizationStats,
    getOrganizationById,
    getOrganizationByCode,
    clearError,
    reset
  }
})
